// URLs relatives : nginx proxifie `^/(decoupled-api|api|jsonapi|…)/` vers Drupal sur
// l'origine du front, donc l'appel reste same-origin — pas de CORS, pas de préflight,
// pas de cookie cross-site. Même convention que WorkflowList.yaml et saveTaskTypeWorkflow.
//
// Le préfixe /api/ correspond à DRUPAL_API_PATTERN dans fetchPatch.js : credentials et
// X-CSRF-Token sont injectés automatiquement pour les requêtes mutantes.
const WORKFLOW_BASE = '/api/orbit-workflow/workflow';
const ROLES_BASE = '/api/orbit-workflow/roles';

/**
 * Loads a workflow configuration (states, transitions, allowedRoles per transition).
 *
 * @param {string} workflowId - The Drupal workflow machine name (e.g. 'doc_editorial').
 * @returns {Promise<Object>} The workflow config object.
 */
export async function fetchWorkflow(workflowId) {
  const res = await fetch(`${WORKFLOW_BASE}/${workflowId}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`Failed to load workflow "${workflowId}": ${res.status}`);
  }
  return res.json();
}

/**
 * Saves a workflow configuration back to Drupal.
 * Updates the workflow config entity and patches role permissions.
 *
 * @param {string} workflowId - The Drupal workflow machine name.
 * @param {Object} config - The workflow config in Drupal format (from workflowConverter.toDrupal).
 * @returns {Promise<Object>} The API response.
 */
export async function saveWorkflow(workflowId, config) {
  const res = await fetch(`${WORKFLOW_BASE}/${workflowId}/save`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      // X-CSRF-Token injected automatically by the global fetchPatch
    },
    body: JSON.stringify(config),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Save failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Fetches all non-system Drupal roles available for permission assignment.
 *
 * @returns {Promise<Array<{id: string, label: string}>>}
 */
export async function fetchRoles() {
  const res = await fetch(`${ROLES_BASE}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`Failed to load roles: ${res.status}`);
  }
  return res.json();
}

/**
 * Lists all workflow configuration entities available in Drupal.
 *
 * @returns {Promise<Array<{id: string, label: string, type: string}>>}
 */
export async function fetchWorkflowList() {
  const res = await fetch(`${WORKFLOW_BASE}`, {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`Failed to load workflow list: ${res.status}`);
  }
  return res.json();
}

/**
 * Assigns or removes the workflow linked to a task_type taxonomy term via the
 * decoupled_toolbox write display (display=decoupled_write).
 *
 * Pass a workflow machine name to assign, or null to unassign.
 *
 * @param {number} taskTypeTid  - TID of the task_type taxonomy term.
 * @param {string|null} workflowId - Workflow machine name to assign, or null to clear.
 * @returns {Promise<Object>} The decoupled-api save response.
 */
export async function saveTaskTypeWorkflow(taskTypeTid, workflowId) {
  const url = `/decoupled-api/save?display=decoupled_write&entity_type=taxonomy_term&bundle=task_type&entity_id=${taskTypeTid}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // credentials + X-CSRF-Token injected automatically by fetchPatch (DRUPAL_API_PATTERN covers /decoupled-api/)
    body: JSON.stringify({ data: { workflow: workflowId ?? null } }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Save task type workflow failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Creates a new orbit_task workflow and links it to a task_type taxonomy term.
 *
 * @param {string} workflowId - Machine name for the new workflow (e.g. 'task_type_evolution').
 * @param {string} label - Human-readable label.
 * @param {number} taskTypeTid - TID of the task_type taxonomy term to link.
 * @returns {Promise<Object>} The created workflow object (same format as fetchWorkflow).
 */
export async function createWorkflow(workflowId, label, taskTypeTid) {
  const res = await fetch(`${WORKFLOW_BASE}/create`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workflow_id: workflowId,
      label,
      task_type_tid: taskTypeTid,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `Create workflow failed: ${res.status}`);
  }
  return res.json();
}
